import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import Constants from '../../services/constants';

// Update the getFunctionVersions thunk
export const getFunctionVersions = createAsyncThunk(
    'edgeFunctions/getVersions',
    async (functionId, { rejectWithValue }) => {
        try {
            const res = await api('get', `${Constants.END_POINT.edge}${functionId}/versions`);
            if (res.success) {
                // Ensure versions have proper timestamps
                const versions = res.data.versions.map(version => ({
                    ...version,
                    timestamp: version.timestamp || new Date().toISOString()
                }));
                return {
                    versions,
                    current_version: res.data.current_version
                };
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const rollbackFunction = createAsyncThunk(
    'edgeFunctions/rollback',
    async ({ id, version }, { rejectWithValue, dispatch }) => {
        try {
            const res = await api('post', `${Constants.END_POINT.edge}${id}/rollback`, { version });
            if (res.success) {
                // Refresh function details after rollback
                await dispatch(fetchFunctionDetails(id)).unwrap();
                return res.data;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

// Previous thunks remain the same...
export const checkFunctionNameAvailability = createAsyncThunk(
    'edgeFunctions/checkName',
    async (name, { rejectWithValue, getState }) => {
        try {
            const orgId = getState()?.org?.active?.id;
            if (!orgId) {
                throw new Error('Organization ID is required');
            }

            const res = await api(
                'get',
                Constants.END_POINT.edge_check_name,
                null,
                {},
                { name, org_id: orgId }
            );

            if (res.success) {
                return { available: res.data.available };
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchEdgeFunctions = createAsyncThunk(
    'edgeFunctions/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api('get', Constants.END_POINT.edge);
            if (res.success) {
                return Array.isArray(res.data) ? res.data : [];
            }
            return rejectWithValue(res.message);
        } catch (error) {
            console.error('Error fetching functions:', error);
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchFunctionDetails = createAsyncThunk(
    'edgeFunctions/fetchDetails',
    async (functionId, { rejectWithValue }) => {
        try {
            const res = await api('get', `${Constants.END_POINT.edge}${functionId}`);
            if (res.success) {
                return res.data;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            console.error('Error fetching function details:', error);
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchFunctionEndpointDetails = createAsyncThunk(
    'edgeFunctions/fetchEndpointDetails',
    async (functionId, { rejectWithValue }) => {
        try {
            const res = await api('get', Constants.END_POINT.edge_endpoint.replace(':id', functionId));
            if (res.success) {
                return res.data;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            console.error('Error fetching endpoint details:', error);
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchFunctionLogs = createAsyncThunk(
    'edgeFunctions/fetchLogs',
    async ({ functionId, limit = 50 }, { rejectWithValue, getState }) => {
        try {
            const details = getState().edgeFunctions.selectedFunction.details;
            if (!details) {
                throw new Error('Function details not loaded');
            }

            const id = details.id;
            if (!id) {
                throw new Error('Invalid function ID');
            }

            const res = await api('get', Constants.END_POINT.edge_logs.replace(':id', id), null, {}, { limit });
            if (res.success) {
                return res.data;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const testFunction = createAsyncThunk(
    'edgeFunctions/test',
    async ({ functionId, params, path = '', method = 'get', testCaseId }, { rejectWithValue }) => {
        try {
            const res = await api(
                'post',
                Constants.END_POINT.edge_test.replace(':id', functionId),
                { params, path, method }
            );
            
            if (res.success) {
                if (testCaseId) {
                    return { result: res.data, testCaseId };
                }
                return res.data;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);


export const fetchFunctionMetrics = createAsyncThunk(
    'edgeFunctions/fetchMetrics',
    async ({ functionId, startTime, endTime }, { rejectWithValue, getState }) => {
        try {
            const details = getState().edgeFunctions.selectedFunction.details;
            if (!details) {
                throw new Error('Function details not loaded');
            }

            const id = details.id;
            if (!id) {
                throw new Error('Invalid function ID');
            }

            const res = await api(
                'get', 
                Constants.END_POINT.edge_metrics.replace(':id', id),
                null,
                {},
                { startTime, endTime }
            );
            
            if (res.success) {
                return {
                    timestamps: res.data.timestamps || [],
                    invocations: res.data.invocations || [],
                    latency: res.data.latency || [],
                    errors: res.data.errors || [],
                    memory: res.data.memory || []
                };
            }
            return rejectWithValue(res.message);
        } catch (error) {
            console.error('Error fetching metrics:', error);
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const createEdgeFunction = createAsyncThunk(
    'edgeFunctions/create',
    async ({ name, runtime, zipFile, params }, { rejectWithValue, dispatch, getState }) => {
        try {
            if (!name || !runtime || !zipFile) {
                throw new Error('Function name, runtime, and zip file are required');
            }

            const orgId = getState()?.org?.active?.id;
            if (!orgId) {
                throw new Error('Organization ID is required');
            }

            const res = await api('post', Constants.END_POINT.edge_create, {
                name,
                runtime,
                zipFile,
                ...params
            }, {}, { org_id: orgId });
            
            if (res.success) {
                await dispatch(fetchEdgeFunctions()).unwrap();
                return res.data;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const updateEdgeFunction = createAsyncThunk(
    'edgeFunctions/update',
    async ({ id, data }, { rejectWithValue, dispatch }) => {
        try {
            const { zipFile, runtime, ...params } = data;
            if (!zipFile || !runtime) {
                throw new Error('Function zip file and runtime are required');
            }

            const res = await api('put', `${Constants.END_POINT.edge_update}${id}`, {
                zipFile,
                runtime,
                ...params
            });
            
            if (res.success) {
                await dispatch(fetchEdgeFunctions()).unwrap();
                return res.data;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const deleteEdgeFunction = createAsyncThunk(
    'edgeFunctions/delete',
    async (functionId, { rejectWithValue, dispatch }) => {
        try {
            const res = await api('delete', `${Constants.END_POINT.edge_delete}${functionId}`);
            if (res.success) {
                await dispatch(fetchEdgeFunctions()).unwrap();
                return functionId;
            }
            return rejectWithValue(res.message);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

const initialState = {
    functions: [],
    selectedFunction: {
        details: null,
        logs: [],
        metrics: {
            timestamps: [],
            invocations: [],
            errors: [],
            latency: [],
            memory: []
        },
        endpointDetails: null,
        testResult: null,
        testCases: [],
        versions: [], // Add versions array
        debugState: {
            isDebugging: false,
            breakpoints: [],
            currentLine: null,
            variables: {},
            callStack: []
        }
    },
    requestHistories: {},
    loading: {
        functions: false,
        details: false,
        logs: false,
        metrics: false,
        test: false,
        update: false,
        endpoint: false,
        create: false,
        nameCheck: false,
        versions: false, // Add versions loading state
        rollback: false  // Add rollback loading state
    },
    error: {
        functions: null,
        details: null,
        logs: null,
        metrics: null,
        test: null,
        update: null,
        endpoint: null,
        create: null,
        nameCheck: null,
        versions: null, // Add versions error state
        rollback: null  // Add rollback error state
    }
};

const edgeFunctionsSlice = createSlice({
    name: 'edgeFunctions',
    initialState,
    reducers: {
        // Keep existing reducers...
        clearErrors: (state) => {
            state.error = initialState.error;
        },
        clearTestResult: (state) => {
            state.selectedFunction.testResult = null;
        },
        clearDebugState: (state) => {
            state.selectedFunction.debugState = initialState.selectedFunction.debugState;
        },
        setBreakpoint: (state, action) => {
            const { line } = action.payload;
            if (!state.selectedFunction.debugState.breakpoints.includes(line)) {
                state.selectedFunction.debugState.breakpoints.push(line);
            }
        },
        removeBreakpoint: (state, action) => {
            const { line } = action.payload;
            state.selectedFunction.debugState.breakpoints = state.selectedFunction.debugState.breakpoints.filter(
                bp => bp !== line
            );
        },
        updateDebugState: (state, action) => {
            state.selectedFunction.debugState = {
                ...state.selectedFunction.debugState,
                ...action.payload
            };
        },
        addTestCase: (state, action) => {
            state.selectedFunction.testCases.push({
                id: Date.now().toString(),
                name: action.payload.name,
                path: action.payload.path,
                method: action.payload.method,
                params: action.payload.params,
                expectedResult: action.payload.expectedResult,
                lastResult: null,
                lastRun: null
            });
        },
        updateTestCase: (state, action) => {
            const index = state.selectedFunction.testCases.findIndex(tc => tc.id === action.payload.id);
            if (index !== -1) {
                state.selectedFunction.testCases[index] = {
                    ...state.selectedFunction.testCases[index],
                    ...action.payload.updates
                };
            }
        },
        deleteTestCase: (state, action) => {
            state.selectedFunction.testCases = state.selectedFunction.testCases.filter(
                tc => tc.id !== action.payload
            );
        },
        addRequestToHistory: (state, action) => {
            const { functionId, ...historyItem } = action.payload;
            
            if (!state.requestHistories[functionId]) {
                state.requestHistories[functionId] = [];
            }

            state.requestHistories[functionId].unshift({
                id: Date.now().toString(),
                timestamp: new Date().toISOString(),
                ...historyItem
            });

            if (state.requestHistories[functionId].length > 50) {
                state.requestHistories[functionId].pop();
            }
        }
    },
    extraReducers: (builder) => {
        // Add new reducers for version management
        builder
            .addCase(getFunctionVersions.pending, (state) => {
                state.loading.versions = true;
                state.error.versions = null;
            })
            .addCase(getFunctionVersions.fulfilled, (state, action) => {
                state.loading.versions = false;
                state.selectedFunction.versions = action.payload.versions;
            })
            .addCase(getFunctionVersions.rejected, (state, action) => {
                state.loading.versions = false;
                state.error.versions = action.payload;
            })

            .addCase(rollbackFunction.pending, (state) => {
                state.loading.rollback = true;
                state.error.rollback = null;
            })
            .addCase(rollbackFunction.fulfilled, (state) => {
                state.loading.rollback = false;
            })
            .addCase(rollbackFunction.rejected, (state, action) => {
                state.loading.rollback = false;
                state.error.rollback = action.payload;
            })

        // Keep all other existing reducers...
            .addCase(checkFunctionNameAvailability.pending, (state) => {
                state.loading.nameCheck = true;
                state.error.nameCheck = null;
            })
            .addCase(checkFunctionNameAvailability.fulfilled, (state) => {
                state.loading.nameCheck = false;
            })
            .addCase(checkFunctionNameAvailability.rejected, (state, action) => {
                state.loading.nameCheck = false;
                state.error.nameCheck = action.payload;
            })

            .addCase(createEdgeFunction.pending, (state) => {
                state.loading.create = true;
                state.error.create = null;
            })
            .addCase(createEdgeFunction.fulfilled, (state) => {
                state.loading.create = false;
            })
            .addCase(createEdgeFunction.rejected, (state, action) => {
                state.loading.create = false;
                state.error.create = action.payload;
            })

            .addCase(fetchEdgeFunctions.pending, (state) => {
                state.loading.functions = true;
                state.error.functions = null;
            })
            .addCase(fetchEdgeFunctions.fulfilled, (state, action) => {
                state.loading.functions = false;
                state.functions = action.payload;
            })
            .addCase(fetchEdgeFunctions.rejected, (state, action) => {
                state.loading.functions = false;
                state.error.functions = action.payload;
                state.functions = [];
            })

            .addCase(fetchFunctionDetails.pending, (state) => {
                state.loading.details = true;
                state.error.details = null;
            })
            .addCase(fetchFunctionDetails.fulfilled, (state, action) => {
                state.loading.details = false;
                state.selectedFunction.details = action.payload;
            })
            .addCase(fetchFunctionDetails.rejected, (state, action) => {
                state.loading.details = false;
                state.error.details = action.payload;
                state.selectedFunction.details = null;
            })

            .addCase(fetchFunctionLogs.pending, (state) => {
                state.loading.logs = true;
                state.error.logs = null;
            })
            .addCase(fetchFunctionLogs.fulfilled, (state, action) => {
                state.loading.logs = false;
                state.selectedFunction.logs = action.payload;
            })
            .addCase(fetchFunctionLogs.rejected, (state, action) => {
                state.loading.logs = false;
                state.error.logs = action.payload;
                state.selectedFunction.logs = [];
            })

            .addCase(fetchFunctionMetrics.pending, (state) => {
                state.loading.metrics = true;
                state.error.metrics = null;
            })
            .addCase(fetchFunctionMetrics.fulfilled, (state, action) => {
                state.loading.metrics = false;
                state.selectedFunction.metrics = action.payload;
            })
            .addCase(fetchFunctionMetrics.rejected, (state, action) => {
                state.loading.metrics = false;
                state.error.metrics = action.payload;
                state.selectedFunction.metrics = null;
            })

            .addCase(testFunction.pending, (state) => {
                state.loading.test = true;
                state.error.test = null;
            })
            .addCase(testFunction.fulfilled, (state, action) => {
                state.loading.test = false;
                if (action.payload.testCaseId) {
                    const testCase = state.selectedFunction.testCases.find(tc => tc.id === action.payload.testCaseId);
                    if (testCase) {
                        testCase.lastResult = action.payload.result;
                        testCase.lastRun = new Date().toISOString();
                    }
                } else {
                    state.selectedFunction.testResult = action.payload;
                }
            })
            .addCase(testFunction.rejected, (state, action) => {
                state.loading.test = false;
                state.error.test = action.payload;
                state.selectedFunction.testResult = null;
            })

            .addCase(updateEdgeFunction.pending, (state) => {
                state.loading.update = true;
                state.error.update = null;
            })
            .addCase(updateEdgeFunction.fulfilled, (state, action) => {
                state.loading.update = false;
                state.selectedFunction.details = action.payload;
            })
            .addCase(updateEdgeFunction.rejected, (state, action) => {
                state.loading.update = false;
                state.error.update = action.payload;
            })

            .addCase(fetchFunctionEndpointDetails.pending, (state) => {
                state.loading.endpoint = true;
                state.error.endpoint = null;
            })
            .addCase(fetchFunctionEndpointDetails.fulfilled, (state, action) => {
                state.loading.endpoint = false;
                state.selectedFunction.endpointDetails = action.payload;
            })
            .addCase(fetchFunctionEndpointDetails.rejected, (state, action) => {
                state.loading.endpoint = false;
                state.error.endpoint = action.payload;
                state.selectedFunction.endpointDetails = null;
            });
    }
});

// Export actions and selectors
export const {
    clearErrors,
    clearTestResult,
    clearDebugState,
    setBreakpoint,
    removeBreakpoint,
    updateDebugState,
    addTestCase,
    updateTestCase,
    deleteTestCase,
    addRequestToHistory
} = edgeFunctionsSlice.actions;

// Memoized selectors
export const selectEdgeFunctionsState = state => state.edgeFunctions;

export const selectFunctions = createSelector(
    [selectEdgeFunctionsState],
    state => state.functions
);

export const selectSelectedFunction = createSelector(
    [selectEdgeFunctionsState],
    state => state.selectedFunction
);

export const selectRequestHistory = createSelector(
    [selectEdgeFunctionsState, (_, functionId) => functionId],
    (state, functionId) => state.requestHistories[functionId] || []
);

export const selectLoading = createSelector(
    [selectEdgeFunctionsState],
    state => state.loading
);

export const selectError = createSelector(
    [selectEdgeFunctionsState],
    state => state.error
);

export default edgeFunctionsSlice.reducer;
